/** @jsx jsx */
import { useState, Fragment } from "react"
import { jsx, Button, useThemeUI } from "theme-ui"
import { LocalizedLink } from "gatsby-theme-i18n"
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from "@reach/accordion"
import { DialogContent, DialogOverlay } from "@reach/dialog"
import { useTransition, animated } from "react-spring"
import VisuallyHidden from "@reach/visually-hidden"

import LocalizedButton from "components/localized-button"
import Bars from "components/svgs/bars"
import Close from "components/svgs/close"

import useLocale from "hooks/use-locales"

import "@reach/accordion/styles.css"
import "@reach/dialog/styles.css"

const styles = {
  menuIcon: {
    height: "1.5rem",
    ml: [0, 0, 2],
    width: "auto",
  },
  link: {
    bg: "none",
    color: "background",
    border: "none",
    display: "block",
    fontSize: [3, 4, 5],
    fontWeight: 700,
    textAlign: "center",
    mb: 2,
  },
}

const Menu = () => {
  const { theme } = useThemeUI()
  const { t } = useLocale()
  const AnimatedDialogOverlay = animated(DialogOverlay)
  const AnimatedDialogContent = animated(DialogContent)
  const [showDialog, setShowDialog] = useState(false)
  const transitions = useTransition(showDialog, null, {
    from: { opacity: 0, y: -10 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: 10 },
  })

  const menuItems = [
    {
      label: t("home"),
      to: "/",
      subItems: [],
    },
    {
      label: t("leadership.title"),
      to: "/leadership-message",
      subItems: [],
    },
    {
      label: t("support.title"),
      subItems: [
        {
          label: t("support.one.title"),
          to: "/supporting-eachother-1",
        },
        {
          label: t("support.two.title"),
          to: "/supporting-eachother-2",
        },
      ],
    },
    {
      label: t("research.title"),
      subItems: [
        {
          label: t("research.one.short"),
          to: "/accelerating-research-1",
        },
        {
          label: t("research.two.short"),
          to: "/accelerating-research-2",
        },
        {
          label: t("research.three.short"),
          to: "/accelerating-research-3",
        },
      ],
    },
    {
      label: t("change.title"),
      subItems: [
        {
          label: t("change.one.title"),
          to: "/mobilizing-1",
        },
        {
          label: t("change.three.title"),
          to: "/mobilizing-2",
        },
        {
          label: t("change.two.title"),
          to: "/mobilizing-3",
        },
      ],
    },
    {
      label: t("partner.title"),
      subItems: [
        {
          label: t("partner.one.title"),
          to: "/partner-1",
        },
        {
          label: t("partner.two.title"),
          to: "/partner-2",
        },
        {
          label: t("partner.three.title"),
          to: "/partner-3",
        },
        {
          label: t("partner.four.title"),
          to: "/partner-4",
        },
      ],
    },
    {
      label: t("impact.title"),
      subItems: [],
      to: "/impact",
    },
  ]

  const path = typeof window !== "undefined" ? window.location.pathname : ""

  return (
    <div>
      <Button
        variant="ghost"
        sx={{ display: "flex", alignItems: "center" }}
        onClick={() => setShowDialog(!showDialog)}
      >
        <VisuallyHidden>{showDialog ? t("close") : t("open")}</VisuallyHidden>
        <span sx={{ display: ["none", "none", "inline"] }}>{t("menu")}</span>
        {showDialog ? (
          <Close sx={styles.menuIcon} />
        ) : (
            <Bars sx={styles.menuIcon} />
          )}
      </Button>
      {transitions.map(
        ({ item, key, props: styles }) =>
          item && (
            <AnimatedDialogOverlay
              key={key}
              style={{
                background: theme.colors.primary,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                opacity: styles.opacity,
              }}
            >
              <AnimatedDialogContent
                aria-label="Menu"
                style={{
                  background: "none",
                  transform: styles.y.interpolate(
                    value => `translate3d(0px, ${value}px, 0px)`
                  ),
                  padding: "0 1rem",
                  margin: "0 auto",
                  width: "90%",
                  maxWidth: "95%",
                }}
              >
                <Accordion
                  collapsible
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    mt: 5,
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  {menuItems.map(item => (
                    <Fragment key={item.label}>
                      {item.subItems.length > 0 ? (
                        <AccordionItem sx={{ mb: 3, textAlign: "center" }}>
                          <AccordionButton
                            sx={{
                              background: "none",
                              borderRadius: "default",
                              color: "background",
                              border: "none",
                              fontSize: [3, 4, 5],
                              fontWeight: 700,
                              mb: 2,
                              py: 2,
                              px: 3,
                              width: "auto",
                              "&:hover": {
                                bg: "background",
                                borderRadius: "default",
                                color: "primary",
                                cursor: "pointer",
                              },
                            }}
                          >
                            {item.label}
                          </AccordionButton>
                          <AccordionPanel>
                            {item.subItems.map(subItem => (
                              <LocalizedButton
                                variant="ghost"
                                key={subItem.label}
                                to={subItem.to}
                                disabled={subItem.to === path}
                                sx={{
                                  m: 2,
                                  width: "calc(50% - 1rem)",
                                  bg: subItem.to === path ? "background" : null,
                                  color:
                                    subItem.to === path ? "secondary" : null,
                                }}
                              >
                                {subItem.label}
                              </LocalizedButton>
                            ))}
                          </AccordionPanel>
                        </AccordionItem>
                      ) : (
                          <div sx={{ mb: 3 }}>
                            <LocalizedLink
                              sx={{
                                borderRadius: "default",
                                color: "background",
                                fontSize: [3, 4, 5],
                                fontWeight: 700,
                                textDecoration: "none",
                                py: 2,
                                px: 3,
                                width: "auto",
                                "&:hover": {
                                  bg: "background",
                                  color: "primary",
                                },
                              }}
                              to={item.to}
                            >
                              {item.label}
                            </LocalizedLink>
                          </div>
                        )}
                    </Fragment>
                  ))}
                </Accordion>
              </AnimatedDialogContent>
            </AnimatedDialogOverlay>
          )
      )}
    </div>
  )
}

export default Menu
